// externals
import { useCallback } from 'react';
import type { MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// selectors
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as vAutoActionCreators from '@makemydeal/dr-dash-store/actions/vAutoActionCreators';
import * as vAutoSelectors from '@makemydeal/dr-dash-store/selectors/vAutoSelectors';

/**
 * Shared launch behavior for the vAuto appraisal, used by every entry point (the appraisal status pill,
 * the collapsed row, and the Trade-In drawer's vAuto link) so they cannot diverge. It opens the in-app
 * appraisal slide-out.
 *
 * The analytics tag fired is based on appraisal state, not the caller: no existing appraisal fires
 * `dc-mv-210` ("Start Appraisal"); resuming an existing one fires `dc-mv-307` ("Saved Appraisal" / "View
 * in vAuto").
 *
 * Entry points pass `keepFocusOnMouseDown` as the element's `onMouseDown`: the links sit directly below
 * trade-in fields that only dispatch on blur (VIN decode, payment calls). Letting the mousedown move
 * focus runs that blur handler first, and the resulting re-render shifts the link out from under the
 * cursor before mouseup, so no click is delivered and the appraisal only opens on the second click.
 */
export const useVAutoAppraisalLaunch = (tradeInIndex: number) => {
    const dispatch = useDispatch();

    const vin = useSelector(tradeInsSelectors.getVin(tradeInIndex));
    const currAppraisal = useSelector(vAutoSelectors.getCurrentAppraisalByVin(vin));
    const vAutoAppraisalStatus = currAppraisal?.status;

    const launchAppraisal = useCallback(() => {
        // launchType must key off `currAppraisal`, matching SlideOutLoaded — NOT `vAutoAppraisalStatus`.
        // They disagree for an empty status, which would break the launch -> load funnel join.
        dispatch(
            vAutoActionCreators.vAutoAppraisalLaunched({
                launchMode: 'slideOut',
                launchType: currAppraisal ? 'existing' : 'new',
                appraisalStatus: vAutoAppraisalStatus || '',
                tradeInIndex,
                hasVin: Boolean(vin)
            })
        );

        dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.Appraisal, undefined, tradeInIndex));
        dispatch(
            vAutoAppraisalStatus
                ? interactionAnalyticsActionCreators.appraiseTradeInVehicleLink()
                : interactionAnalyticsActionCreators.startAppraisalLink()
        );
    }, [dispatch, tradeInIndex, vin, currAppraisal, vAutoAppraisalStatus]);

    const keepFocusOnMouseDown = useCallback((event: MouseEvent) => {
        event.preventDefault();
    }, []);

    return { launchAppraisal, keepFocusOnMouseDown };
};
